import React, { useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { chatService } from "../service/chatService";
import { useStartConversation } from "../viewmodel/useChatViewModel";
import { UserAutocompleteResult } from "../types";
import styles from "./NewConversationModal.module.scss";

interface Props {
  visible: boolean;
  onHide: () => void;
}

export const NewConversationModal: React.FC<Props> = ({ visible, onHide }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<UserAutocompleteResult[]>([]);
  const [searching, setSearching] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { startConversation, loading: starting } = useStartConversation();

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!val.trim()) {
      setResults([]);
      return;
    }
    searchTimerRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const found = await chatService.searchUsers(val);
        setResults(found);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
  };

  const handleSelectUser = async (user: UserAutocompleteResult) => {
    onHide();
    await startConversation(user.uid);
  };

  const handleHide = () => {
    setQuery("");
    setResults([]);
    onHide();
  };

  return (
    <Dialog
      header="New conversation"
      visible={visible}
      onHide={handleHide}
      className={styles.dialog}
      draggable={false}
      resizable={false}
    >
      <div className={styles.searchWrap}>
        <i className="pi pi-search" />
        <input
          autoFocus
          type="text"
          placeholder="Search by name..."
          value={query}
          onChange={handleQueryChange}
          className={styles.searchInput}
        />
        {searching && (
          <ProgressSpinner style={{ width: "18px", height: "18px" }} />
        )}
      </div>

      <div className={styles.results}>
        {!searching && query.trim() && results.length === 0 && (
          <div className={styles.empty}>No users found</div>
        )}
        {results.map((user) => {
          const fullName =
            [user.firstname, user.lastname].filter(Boolean).join(" ") ||
            `User ${user.uid}`;
          return (
            <button
              key={user.uid}
              className={styles.userRow}
              onClick={() => handleSelectUser(user)}
              disabled={starting}
            >
              <div className={styles.avatar}>
                {user.logo_uri ? (
                  <img src={user.logo_uri} alt={fullName} />
                ) : (
                  <i className="pi pi-user" />
                )}
              </div>
              <div className={styles.userInfo}>
                <span className={styles.userName}>{fullName}</span>
                {user.job_title && (
                  <span className={styles.userJob}>{user.job_title}</span>
                )}
              </div>
              {starting && (
                <ProgressSpinner style={{ width: "16px", height: "16px" }} />
              )}
            </button>
          );
        })}
      </div>
    </Dialog>
  );
};
